import { TextField, TextFieldTypes } from '@comptia-sso/core';
import {
  ChangeEvent,
  ReactElement,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Types.
import { IInstructor } from 'types';

// Styles.
import styles from './ConsentManagementPrompt.module.scss';

interface IInstructorSelectProps {
  error?: string;
  instructors: IInstructor[];
  label: string;
  onChange: (email: string) => void;
  placeholder: string;
  value: string;
}

const getInstructorLabel = (instructor: IInstructor): string => {
  const name = [instructor.firstName, instructor.lastName]
    .filter((part) => part.length > 0)
    .join(' ');
  return name ? `${name} - ${instructor.email}` : instructor.email;
};

/**
 * A filterable combobox that lists the COPPA instructors tied to the user's
 * institution beneath the parent/guardian email field. Typing filters the
 * list and selecting an instructor fills the field with only the email.
 * Mirrors the behaviour of the B2C `injectInstructorSelect` utility.
 */
export const InstructorSelect = ({
  error,
  instructors,
  label,
  onChange,
  placeholder,
  value,
}: IInstructorSelectProps): ReactElement => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const blurTimeout = useRef<ReturnType<typeof setTimeout>>();
  const listRef = useRef<HTMLUListElement>(null);
  const [listHeight, setListHeight] = useState<number>(0);

  const filteredInstructors = useMemo(() => {
    const query = value.trim().toLowerCase();

    // If the value exactly matches an instructor's email (e.g. a just-selected
    // value), keep the full list visible so it can still be changed.
    const exactMatch = instructors.some(
      (instructor) => instructor.email.toLowerCase() === query,
    );

    if (!query || exactMatch) {
      return instructors;
    }

    return instructors.filter((instructor) =>
      getInstructorLabel(instructor).toLowerCase().includes(query),
    );
  }, [instructors, value]);

  const handleSelect = (email: string): void => {
    onChange(email);
    setIsOpen(false);
  };

  const isListVisible = isOpen && filteredInstructors.length > 0;

  // Reserve space beneath the field for the absolutely-positioned dropdown so
  // it isn't clipped by the modal's scroll container and can be scrolled to.
  useLayoutEffect(() => {
    setListHeight(isListVisible ? (listRef.current?.offsetHeight ?? 0) + 8 : 0);
  }, [isListVisible, filteredInstructors, value]);

  return (
    <div className={styles.instructorSelect}>
      <div className={styles.instructorField}>
        <TextField
          error={error}
          label={label}
          onBlur={() => {
            // Delay closing so an option click registers first.
            blurTimeout.current = setTimeout(() => setIsOpen(false), 150);
          }}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            onChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (blurTimeout.current) {
              clearTimeout(blurTimeout.current);
            }
            setIsOpen(true);
          }}
          type={TextFieldTypes.Email}
          value={value}
        />
        {isListVisible && (
          <ul className={styles.instructorList} ref={listRef} role="listbox">
            <li className={styles.instructorPlaceholder} aria-disabled="true">
              {placeholder}
            </li>
            {filteredInstructors.map((instructor) => (
              <li
                className={styles.instructorOption}
                key={instructor.email}
                onMouseDown={(e) => {
                  // Use mousedown so selection happens before the input blur.
                  e.preventDefault();
                  handleSelect(instructor.email);
                }}
                role="option"
                title={getInstructorLabel(instructor)}
              >
                {getInstructorLabel(instructor)}
              </li>
            ))}
          </ul>
        )}
      </div>
      {isListVisible && (
        <div
          aria-hidden="true"
          className={styles.instructorSpacer}
          style={{ height: listHeight }}
        />
      )}
    </div>
  );
};
